import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CalendarClock, History, MoreHorizontal, UserPlus } from "lucide-react";
import useIsPhone from "features/files/use-is-phone";
import UserSelect from "features/user-select/user-select";
import { AuditLogPopover } from "components/audit-log/audit-log-popover";
import ProjectBoardSwitcher from "components/project/project-board-switcher/project-board-switcher";

const SECONDARY_BUTTON =
  "inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-3 py-2 text-sm font-semibold text-[var(--p-color-text)] hover:bg-[var(--p-color-bg-surface-secondary)] max-md:min-h-11";
const PRIMARY_BUTTON =
  "shrink-0 rounded-md bg-[var(--p-color-bg-fill-brand)] px-3 py-2 text-sm font-semibold text-[var(--p-color-text-brand-on-bg-fill)] hover:bg-[var(--p-color-bg-fill-brand-hover)] max-md:min-h-11";
const HEADER_ROW =
  "flex flex-wrap items-center gap-3 border-b border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-4 py-3";

interface ProjectBoardHeaderProps {
  companyId: string;
  boardId: string;
  boardName: string;
  dealCount: number;
  selectedUser: string | undefined;
  onUserFilterChange: (value: unknown, key: string) => void;
  onOpenAgenda: () => void;
  onShare: () => void;
  onCreateCard: () => void;
}

/**
 * Top bar of a board. On a desktop everything sits in one row. On a phone the
 * row keeps only the way back, the board switcher and «New card»; the user
 * filter, History, Agenda, Opportunities and Share open under a «...» button.
 */
export default function ProjectBoardHeader({
  companyId,
  boardId,
  boardName,
  dealCount,
  selectedUser,
  onUserFilterChange,
  onOpenAgenda,
  onShare,
  onCreateCard,
}: ProjectBoardHeaderProps) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  // The filter lives in redux and keeps filtering the board while the section is folded.
  const isUserFilterOn = Boolean(selectedUser);

  const backLink = (
    <Link
      to={`/app/${companyId}/project-boards`}
      className="shrink-0 rounded px-2 py-1 text-sm font-medium text-[var(--p-color-text-secondary)] hover:bg-[var(--p-color-bg-surface-secondary)] max-md:flex max-md:min-h-11 max-md:items-center"
    >
      ← {t("project-boards", { defaultValue: "Boards" })}
    </Link>
  );
  const history = (
    <div className="inline-flex shrink-0 items-center max-md:self-start">
      <AuditLogPopover
        subjectId={boardId}
        subjectType="ProjectBoard,ProjectBoardColumn,Project"
        label={t("board_history", { defaultValue: "History" })}
      />
    </div>
  );
  const switcher = (
    <ProjectBoardSwitcher
      companyId={companyId}
      boardId={boardId}
      boardName={boardName}
      dealCount={dealCount}
    />
  );
  const userFilter = (
    <div className={isPhone ? "w-full text-slate-900" : "w-56 shrink-0 text-slate-900"}>
      <UserSelect
        value={selectedUser}
        name="user"
        onChange={onUserFilterChange}
        isRequired={false}
        allowMultiple
        label="select_user"
        labelHidden
      />
    </div>
  );
  const agendaButton = (
    <button type="button" onClick={onOpenAgenda} className={SECONDARY_BUTTON}>
      <CalendarClock size={16} strokeWidth={1.75} />
      {t("project_agenda", { defaultValue: "Agenda" })}
    </button>
  );
  const opportunitiesLink = (
    <Link
      to={`/app/${companyId}/project-boards/opportunity-history?boardId=${boardId}`}
      className={SECONDARY_BUTTON}
    >
      <History size={16} strokeWidth={1.75} />
      {t("project_opportunity_history_short", {
        defaultValue: "Opportunities",
      })}
    </Link>
  );
  const shareButton = (
    <button type="button" onClick={onShare} className={SECONDARY_BUTTON}>
      <UserPlus size={16} strokeWidth={1.75} />
      {t("share", { defaultValue: "Share" })}
    </button>
  );
  const newCardButton = (
    <button type="button" onClick={onCreateCard} className={PRIMARY_BUTTON}>
      {t("project-create", { defaultValue: "New card" })}
    </button>
  );

  if (isPhone) {
    return (
      <div className={HEADER_ROW}>
        {backLink}
        <div className="ml-auto flex items-center gap-2">
          {newCardButton}
          <button
            type="button"
            aria-expanded={isMoreOpen}
            aria-label={
              isUserFilterOn
                ? `${t("more", { defaultValue: "More" })} (${t("filters", { defaultValue: "Filter" })})`
                : t("more", { defaultValue: "More" })
            }
            onClick={() => setIsMoreOpen((isOpen) => !isOpen)}
            className={`${SECONDARY_BUTTON} relative max-md:min-w-11 max-md:justify-center`}
          >
            <MoreHorizontal size={18} strokeWidth={1.75} />
            {isUserFilterOn && (
              <span
                aria-hidden
                className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-[var(--p-color-bg-fill-brand)]"
              />
            )}
          </button>
        </div>
        <div className="w-full">{switcher}</div>
        {isMoreOpen && (
          <div className="flex w-full flex-col gap-2">
            {history}
            {userFilter}
            <div className="flex flex-wrap gap-2">
              {agendaButton}
              {opportunitiesLink}
              {shareButton}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={HEADER_ROW}>
      {backLink}
      {history}
      {switcher}
      <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2">
        {userFilter}
        {agendaButton}
        {opportunitiesLink}
        {shareButton}
        {newCardButton}
      </div>
    </div>
  );
}
